<script setup lang="ts">
type Props = {
  tableType: string;
  items: ITransaction[];
};

const { tableType = '', items = [] } = defineProps<Props>();

const { t } = useTexts();
const { showMoneyFormat } = useWallet();

const hasPlayerDesc = computed(() => {
  return items.some((item: ITransaction) => !!item.player_desc);
});

const hasDescription = computed(() => {
  return items.some((item: ITransaction) => !!item.description);
});

const hasIsBonus = computed(() => {
  return items.some((item: ITransaction) => 'is_bonus' in item);
});

const hasUpdatedAt = computed(() => {
  return items.some((item: ITransaction) => !!item.updated_at);
});

const hasCreatedAt = computed(() => {
  return items.some((item: ITransaction) => !!item.created_at);
});

const tableColumns = computed(() => {
  const columns: { key: string; label: string }[] = [];

  columns.push({
    key: 'type',
    label: t('account.wallet.table.fields.type'),
  });

  columns.push({
    key: 'id',
    label: 'ID',
  });

  if (hasPlayerDesc.value) {
    const labelDesc = tableType.match(/casino/gi)
      ? t('account.wallet.table.fields.game_name')
      : t('account.wallet.table.fields.source');

    columns.push({
      key: 'player_desc',
      label: labelDesc,
    });
  } else if (hasDescription.value) {
    columns.push({
      key: 'description',
      label: t('account.wallet.table.fields.description'),
    });
  }

  columns.push({
    key: 'amount',
    label: t('account.wallet.table.fields.amount'),
  });

  columns.push({
    key: 'status',
    label: t('account.wallet.table.fields.status'),
  });

  if (hasIsBonus.value) {
    columns.push({
      key: 'is_bonus',
      label: t('account.wallet.table.fields.is_bonus'),
    });
  }

  if (hasCreatedAt.value) {
    columns.push({
      key: 'created_at',
      label: t('account.wallet.table.fields.created_at'),
    });
  } else if (hasUpdatedAt.value) {
    columns.push({
      key: 'updated_at',
      label: t('account.wallet.table.fields.updated_at'),
    });
  }

  if (tableType.match(/withdraw/gi) && isDetailButtonVisible.value) {
    columns.push({
      key: 'details',
      label: t('account.wallet.table.fields.details'),
    });
  }

  columns.push({
    key: 'round',
    label: t('account.wallet.table.fields.round'),
  });

  columns.push({
    key: 'reference',
    label: t('account.wallet.table.fields.reference'),
  });

  return columns;
});

const currentFields = computed(() => {
  if (!tableType) return [];

  return tableColumns.value;
});

function getTransactionType(status: ITransaction['type']) {
  const transactionType: any = {
    DEFAULT: {
      variant: 'danger',
      icon: 'info',
    },
    processing: {
      variant: 'warning',
      icon: 'info',
    },
    pending: {
      variant: 'warning',
      icon: 'info',
    },
    approved: {
      variant: 'success',
      icon: 'info',
    },
    credit: {
      variant: 'success',
      icon: 'arrows/arrow_up',
    },
    debit: {
      variant: 'danger',
      icon: 'arrows/arrow_down',
    },
  };

  return transactionType[status] || transactionType.DEFAULT;
}

function getStatus(statusKey: string = 'default') {
  const status: any = {
    default: {
      text: '---',
      variant: 'warning',
    },
    pending: {
      text: t('account.wallet.table.status.pending'),
      variant: 'warning',
    },
    approved: {
      text: t('account.wallet.table.status.approved'),
      variant: 'success',
    },
    denied: {
      text: t('account.wallet.table.status.denied'),
      variant: 'danger',
    },
    error: {
      text: t('account.wallet.table.status.error'),
      variant: 'danger',
    },
    expired: {
      text: t('account.wallet.table.status.expired'),
      variant: 'danger',
    },
    rolled_back: {
      text: t('account.wallet.table.status.canceled'),
      variant: 'danger',
    },
    processing: {
      variant: 'warning',
      text: t('account.wallet.table.status.processing'),
    },
    credit: {
      text: t('account.wallet.table.status.credit'),
      variant: 'info',
    },
    debit: {
      text: t('account.wallet.table.status.debit'),
      variant: 'danger',
    },
    transfer: {
      text: t('account.wallet.table.status.transfer'),
      variant: 'success',
    },
  };

  return status[statusKey] || status.default;
}

function getTransactionStatusVariant(item: ITransaction) {
  if (item?.status) {
    return getStatus(item.status.toLowerCase()).variant
      ? getStatus(item.status.toLowerCase()).variant
      : 'warning';
  }

  return 'warning';
}

function getTransactionStatusText(item: ITransaction) {
  if (item?.status) {
    return getStatus(item.status.toLowerCase()).text
      ? getStatus(item.status.toLowerCase()).text
      : '---';
  }

  return '-';
}

function dateFilter(date: string) {
  if (!date) return '---';

  const dataFornecida = new Date(date);
  const fusoHorarioUsuario = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const dataLocalUsuario = dataFornecida.toLocaleString(currentLocale(), {
    timeZone: fusoHorarioUsuario,
  });

  return dataLocalUsuario;
}

function checkFieldName(value: string) {
  if (currentFields.value.filter((e: any) => e.key === value).length > 0) {
    return true;
  }

  return false;
}

const { isWithdrawalReceiptEnabled } = useFeatureFlagsEnv();

const isDetailButtonVisible = computed<boolean>(() => {
  return isWithdrawalReceiptEnabled.value;
});

function isDetailButtonEnabled(item: ITransaction): boolean {
  return item.status === 'approved' && !!item.has_receipt;
}

function getAmountFieldValue(item: ITransaction) {
  if (!!item.is_freebet || !!item.is_freespin) {
    return item.amount;
  }

  return showMoneyFormat({ value: item.amount / 100 });
}

const isWithdrawDetailsDialogVisible = ref<boolean>(false);
const base64File = ref<string>('');

function onDetailsButtonClick(item: ITransaction) {
  if (!item.src_id) return;

  const { ApiService } = useApiService();

  isWithdrawDetailsDialogVisible.value = true;

  ApiService.get(`/withdraw/${item.src_id}/generate`)
    .then((res: any) => {
      base64File.value = res.data;
    })
    .catch(() => {
      isWithdrawDetailsDialogVisible.value = false;
    });
}
</script>

<template>
  <div :class="$style.walletTableResponsive">
    <table
      v-if="currentFields.length"
      :class="$style.walletTable"
    >
      <thead>
        <tr :class="$style.tableHeader">
          <th
            v-for="field in currentFields"
            :key="field.key"
          >
            {{ field.label }}
          </th>
        </tr>
      </thead>

      <tbody>
        <tr
          v-for="item in items"
          :key="item.id"
          :class="$style.tableItem"
        >
          <td
            v-if="checkFieldName('type')"
            data-name="type"
            style="width: 50px; text-align: center"
          >
            <div
              :class="[
                $style.tableBadge,
                $style[getTransactionType(item.type).variant],
              ]"
            >
              <NuxtIcon :name="getTransactionType(item.type).icon" />
            </div>
          </td>

          <td
            v-if="checkFieldName('id')"
            data-name="id"
          >
            {{ item.id }}
          </td>

          <td
            v-if="checkFieldName('player_desc')"
            data-name="player_desc"
          >
            {{ item.player_desc }}
          </td>

          <td
            v-if="checkFieldName('description')"
            data-name="description"
          >
            {{ item.description }}
          </td>

          <td
            v-if="checkFieldName('amount')"
            data-name="amount"
          >
            {{ getAmountFieldValue(item) }}
          </td>

          <td
            v-if="checkFieldName('status')"
            data-name="status"
          >
            <div
              :class="[
                $style.tableBadge,
                $style[getTransactionStatusVariant(item)],
              ]"
            >
              <small>{{ getTransactionStatusText(item) }}</small>
            </div>
          </td>

          <td
            v-if="checkFieldName('is_bonus')"
            data-name="is_bonus"
          >
            {{ item.is_bonus ? t('others.yes') : t('others.no') }}
          </td>

          <td
            v-if="checkFieldName('updated_at')"
            data-name="updated_at"
          >
            {{ dateFilter(item.updated_at || '') }}
          </td>

          <td
            v-if="checkFieldName('created_at')"
            data-name="created_at"
          >
            {{ dateFilter(item.created_at || '') }}
          </td>

          <td
            v-if="checkFieldName('details') && isDetailButtonVisible"
            data-name="details"
            style="width: 70px; text-align: center"
          >
            <button
              :class="[
                $style.detailsButton,
                {
                  [$style.isApproved]: item.status === 'approved',
                  [$style.isDenied]: item.status === 'denied',
                  [$style.isPending]: item.status === 'pending',
                  [$style.isProcessing]: item.status === 'processing',
                  [$style.disabled]: !isDetailButtonEnabled(item),
                },
              ]"
              :disabled="!isDetailButtonEnabled(item)"
              @click="onDetailsButtonClick(item)"
            >
              <NuxtIcon name="document" />
            </button>
          </td>
          <td
            v-if="checkFieldName('round')"
            data-name="round"
          >
            {{
              item.round_id
                ? item.round_id
                : t('account.wallet.table.fields.unavailable')
            }}
          </td>
          <td
            v-if="checkFieldName('reference')"
            data-name="reference"
          >
            {{
              item.reference
                ? item.reference
                : t('account.wallet.table.fields.unavailable')
            }}
          </td>
        </tr>
      </tbody>
    </table>
  </div>

  <UserWalletWithdrawDetailsDialog
    v-if="isWithdrawDetailsDialogVisible"
    v-model="base64File"
    @close="isWithdrawDetailsDialogVisible = false"
  />
</template>

<style module lang="postcss">
.walletTableResponsive {
  @apply w-full overflow-x-auto;

  .walletTable {
    @apply w-full my-2 gap-1 border-separate border-spacing-y-1.5;

    .tableHeader {
      th {
        @apply py-3 px-4 text-left text-xs font-semibold whitespace-nowrap bg-primary/5 text-primary/70;

        &:first-child {
          @apply rounded-l-md;
        }
        &:last-child {
          @apply rounded-r-md;
        }
      }
    }

    .tableItem {
      td {
        @apply py-3 px-3 text-left text-xs md:text-sm font-medium align-top whitespace-nowrap bg-black/10;

        &:first-child {
          @apply rounded-l-md;
        }
        &:last-child {
          @apply rounded-r-md;
        }
      }

      &:nth-child(odd) {
        td {
          @apply bg-black/5;
        }
      }
    }

    .tableBadge {
      @apply text-xs font-semibold p-1 inline-flex rounded leading-3;

      &.success {
        @apply bg-success;
      }
      &.warning {
        @apply bg-warning text-black;
      }
      &.danger {
        @apply bg-error;
      }
      &.expired {
        @apply bg-info;
      }

      svg {
        @apply text-xs;
      }

      small {
        @apply leading-3 -my-px;
      }
    }
  }
}

.detailsButton {
  @apply p-1 rounded-md hover:opacity-70 bg-button-bg/15 text-sm;

  &.isApproved {
    @apply text-button-bg;
  }

  &.isDenied {
    @apply text-error bg-error/15;
  }

  &.isPending {
    @apply text-warning bg-warning/15;
  }

  &.isProcessing {
    @apply text-warning bg-warning/15;
  }

  &.disabled {
    @apply opacity-40 cursor-not-allowed;
  }
}
</style>
